import React, { useEffect, useRef, useState } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import { MdOutlineNoPhotography } from 'react-icons/md';
import {
  Divider,
  IconButton,
  Pagination,
  Modal,
  Box,
  Tab,
  Tabs,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from '@mui/material';
import { IoCloseOutline } from 'react-icons/io5';
import objectStore from '../../stores/objectStore';
import { observer } from 'mobx-react-lite';
import RenderSelectedTags from '../settings/tags/RenderSelectedTags';

import ModalObjectCard from './ModalObjectCard';
import statusStore from '../../stores/statusStore';
import { Select, MenuItem } from '@mui/material';
import requestStore from '../../stores/requestStore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import logger from '../../utils/logger';
import { getPrice } from '../../utils/priceUtils';
import { getObjectFullInfo } from '../../utils/objectInfoUtils';
import { FaRegTrashCan } from 'react-icons/fa6';
import { scopedObjectSource } from '../../tenancy/scopedObjectDto';
import { requestObjectMatchingLabels } from '../../utils/requestObjectMatchingPresentation';


//const App = observer(() => {
// const ListObjectActiveRequest = observer(({ objects, requestId }) => {

function ListObjectActiveRequest({ requestId }) {
  const statuses = statusStore.statuses;
  const isMounted = useRef(true);
  const [object, setObject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [objectToDelete, setObjectToDelete] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallMobile = useMediaQuery('(max-width:360px)');

  // Состояние для пагинации доступных объектов
  const [availablePage, setAvailablePage] = useState(1);

  const statusOrder = [
    { id: 'completed', label: 'Завершенные' },
    { id: 'in_progress', label: 'В процессе' },
    { id: 'scheduled_viewing', label: 'На просмотр' },
    { id: 'new', label: 'Новые объекты' },
    { id: 'under_review', label: 'На рассмотрении' },
    { id: 'not_suitable', label: 'Не подходят' },
  ];

  const statusColors = {
    completed: '#4CAF50', // зеленый
    in_progress: '#2196F3', // синий
    scheduled_viewing: '#a8bd20', // желтый
    new: '#9C27B0', // фиолетовый
    under_review: '#FF9800', // оранжевый
    not_suitable: '#F44336', // красный
    available: '#607D8B', // серый для доступных объектов
  };

  const selectableStatuses = statusOrder.filter((status) => status.id !== 'new');

  const activeRequestObjects = requestStore.activeRequestObjects;
  const availableObjects = requestStore.activeRequestObjectsAvailable;

  const groupedObjects = statusOrder
    .map((statusItem) => {
      const filtered = activeRequestObjects.filter((obj) => obj.status === statusItem.id);
      return { ...statusItem, objects: filtered };
    })
    .filter((group) => group.objects.length > 0);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      objectStore.objectsForRequest = [];
    };
  }, []);

  // Загрузка доступных объектов при монтировании компонента и при изменении страницы
  useEffect(() => {
    if (requestId) {
      requestStore.fetchAvailableObjects(requestId, availablePage);
    }
  }, [requestId, availablePage]);

  const handleOpenObject = async (objectId) => {
    const obj = [...activeRequestObjects, ...availableObjects].find((obj) => obj.id === objectId);
    setObject(obj);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setObject(null);
  };

  const handleStatusChange = (event, obj) => {
    const newStatus = event.target.value;
    requestStore.updateObjectStatus(obj.requestId, obj.id, newStatus);
  };

  const handleAvailableStatusChange = (event, obj) => {
    const newStatus = event.target.value;
    requestStore.setNewStatusOnAvailableObject(requestId, obj.id, newStatus);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Обработчик изменения страницы для доступных объектов
  const handleAvailablePageChange = (event, page) => {
    setAvailablePage(page);
  };

  // Функция для получения краткого описания (первые 100 символов)
  const getShortDescription = (description) => {
    if (!description) return '';
    return description.length > 100 ? `${description.substring(0, 100)}...` : description;
  };

  const renderObjectList = (objList, isAvailable = false) => (
    <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2 }}>
      {objList.map((obj, index) => {
		const objectId = obj.id ?? obj.objectId;
		const matchingLabels = requestObjectMatchingLabels(obj);
		if (obj.availability === 'UNAVAILABLE') {
			return (
				<React.Fragment key={`unavailable-${objectId}`}>
					<ListItem sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
						<Box>
						<Typography fontWeight={600}>Объект недоступен</Typography>
						<Typography variant="body2" color="text.secondary">Доступ был отозван; данные объекта скрыты.</Typography>
						{matchingLabels.map(label => <Typography key={label} variant="caption" color={label === 'Добавлен вручную' ? 'primary' : 'warning.main'} sx={{ display: 'block' }}>{label}</Typography>)}
						</Box>
						<IconButton onClick={() => handleDelete(objectId)} aria-label="Убрать недоступный объект из заявки">
							<FaRegTrashCan size={18} className="text-red-500" />
						</IconButton>
					</ListItem>
					{index < objList.length - 1 && <Divider />}
				</React.Fragment>
			);
		}
        // Используем imageUrl напрямую, если он есть
        const photoUrl = obj.photos?.[0]?.imageUrl || null; // Используем постоянный public URL

        return (
		  <React.Fragment key={objectId}>
            <ListItem
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                cursor: 'pointer',
                padding: isMobile ? '8px' : '16px',
              }}
			  onClick={() => handleOpenObject(objectId)}>
              {isMobile ? (
                // Мобильная версия - фото вверху, описание внизу
                <>
                  {photoUrl ? (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 1 }}>
                      <Avatar
                        variant="rounded"
                        src={photoUrl}
                        sx={{
                          width: '100%',
                          height: isSmallMobile ? '150px' : '180px',
                          borderRadius: '8px',
                        }}
                      />
                    </Box>
                  ) : null}
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        mb: 0.5,
                        fontSize: '0.7rem',
                      }}>
                      {getObjectFullInfo(obj, objectStore.categories)}
                    </Typography>
                    <div className={isSmallMobile ? 'text-base' : 'text-lg'}>
                      {obj.renderTitle}{' '}
                    </div>
                    <div className="text-sm ">{obj.address}</div>
                    {obj.description && !isSmallMobile && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                        {getShortDescription(obj.description)}
                      </Typography>
                    )}
                    <div className="font-bold mt-1">
                      {getPrice(obj.details).value} {getPrice(obj.details).suffix}
                    </div>
                    {scopedObjectSource(obj.source) && (
                      <Typography
                        sx={{ marginTop: 1, fontSize: isSmallMobile ? '0.75rem' : '0.875rem' }}>
                        Источник:{' '}
                        <strong className="text-blue-500 font-bold underline">
                          {scopedObjectSource(obj.source).name}
                        </strong>
                      </Typography>
                    )}
                    <Box sx={{ mt: 1 }}>
                      <RenderSelectedTags tags={obj.details?.tags} />
                    </Box>
					{matchingLabels.map(label => <Typography key={label} variant="caption" color={label === 'Добавлен вручную' ? 'primary' : 'warning.main'} sx={{ display: 'block', mt: 1 }}>{label}</Typography>)}
                  </Box>
                </>
              ) : (
                // Десктопная версия - оставляем как было
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                  <ListItemAvatar>
                    {photoUrl ? (
                      <Avatar
                        variant="rounded"
                        src={photoUrl}
                        sx={{ width: '60px', height: '60px' }}
                      />
                    ) : (
                      <Avatar sx={{ width: '60px', height: '60px' }}>
                        <MdOutlineNoPhotography className="text-3xl" />
                      </Avatar>
                    )}
                  </ListItemAvatar>
                  <Box
                    sx={{
                      display: 'flex',
                      flex: 1,
                      justifyContent: 'space-between',
                      pl: 2,
                    }}>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          mb: 1,
                          fontSize: '0.7rem',
                        }}>
                        {getObjectFullInfo(obj, objectStore.categories)}
                      </Typography>
                      <div className="text-lg font-bold">{obj.renderTitle} </div>
                      <div className="text-sm ">{obj.address}</div>
                      {obj.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                          {getShortDescription(obj.description)}
                        </Typography>
                      )}
                      <div>
                        {getPrice(obj.details).value} {getPrice(obj.details).suffix}
                      </div>
                      {scopedObjectSource(obj.source) && (
                        <Typography sx={{ marginTop: 1 }}>
                          Источник:{' '}
                          <strong className="text-blue-500 font-bold">
                            {scopedObjectSource(obj.source).name}
                          </strong>
                        </Typography>
                      )}
                      <Box sx={{ mt: 1 }}>
                        <RenderSelectedTags tags={obj.details?.tags} />
                      </Box>
					  {matchingLabels.map(label => <Typography key={label} variant="caption" color={label === 'Добавлен вручную' ? 'primary' : 'warning.main'} sx={{ display: 'block', mt: 1 }}>{label}</Typography>)}
                    </Box>
                  </Box>
                </Box>
              )}
              <Box sx={{ mt: 1, width: '100%' }} onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-row items-center justify-between w-full">
                  <Select
                    size="small"
                    value={obj.status === 'new' ? '' : obj.status || ''}
                    onChange={(event) =>
                      isAvailable
                        ? handleAvailableStatusChange(event, obj)
                        : handleStatusChange(event, obj)
                    }
                    sx={{ width: isMobile ? 'calc(100% - 40px)' : 'auto' }}
                    displayEmpty>
                    {isAvailable ? (
                      <MenuItem value="">
                        <em>Задать статус</em>
                      </MenuItem>
                    ) : (
                      <MenuItem value="">
                        <em>Задать статус</em>
                      </MenuItem>
                    )}
                    {selectableStatuses.map((statusItem) => (
                      <MenuItem key={statusItem.id} value={statusItem.id}>
                        {statusItem.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {!isAvailable && (
                    <IconButton
                      onClick={() => handleDelete(obj.id)}
                      size={isMobile ? 'small' : 'medium'}>
                      <FaRegTrashCan size={18} className="text-red-500" />
                    </IconButton>
                  )}
                </div>
              </Box>
            </ListItem>
            {index < objList.length - 1 && <Divider />}
          </React.Fragment>
        );
      })}
    </List>
  );

  const handleDelete = (objectId) => {
    setObjectToDelete(objectId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    logger.log('🎀 handleDelete', objectToDelete);
    requestStore.deleteObjectFromActiveRequest(objectToDelete, requestId);
    setDeleteDialogOpen(false);
    setObjectToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setObjectToDelete(null);
  };

  return (
    <>
      {import.meta.env.VITE_SHOW_COMPONENT_NAME === 'true' && (
        <div onClick={() => logger.log('ListObjectActiveRequest Render')}>
          % ListObjectActiveRequest %
        </div>
      )}

      {/* Группировка активных объектов по статусам */}
      {groupedObjects.map((statusGroup) => (
        <Accordion
          key={statusGroup.id}
          sx={{
            position: 'relative',
            marginBottom: 1,
            borderLeft: `3px solid ${statusColors[statusGroup.id]}`,
            '& .MuiAccordionSummary-root': {
              paddingLeft: '16px',
            },
            '& .MuiCollapse-root': {
              position: 'relative',
            },
          }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              {statusGroup.label} ({statusGroup.objects.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: isMobile ? 1 : 2 }}>
            {renderObjectList(statusGroup.objects, false)}
          </AccordionDetails>
        </Accordion>
      ))}
      {availableObjects.length > 0 && (
        <>
          <p className="text-sm pt-4 pb-1">Объекты ниже доступны для добавления в заявку</p>
          <Accordion
            key="available"
          sx={{
            position: 'relative',
            borderLeft: `4px solid ${statusColors.available}`,
            '& .MuiAccordionSummary-root': {
              paddingLeft: '16px',
            },
            '& .MuiCollapse-root': {
              position: 'relative',
            },
          }}>
      
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Доступные объекты ({availableObjects.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderObjectList(availableObjects, true)}

            {/* Пагинация для доступных объектов */}
            {requestStore.totalPagesAvailable > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={requestStore.totalPagesAvailable}
                  page={availablePage}
                  onChange={handleAvailablePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                />
              </Box>
            )}
            </AccordionDetails>
          </Accordion>
        </>
      )}
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={(theme) => ({
            position: 'absolute',
            top: '5%',
            left: '50%',
            transform: 'translate(-50%, 0)',
            width: '97%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 3,
            maxHeight: '95vh',
            overflowY: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            [theme.breakpoints.up('sm')]: { width: '80%' },
            [theme.breakpoints.up('md')]: { width: '70%' },
            [theme.breakpoints.up('lg')]: { width: '60%' },
            [theme.breakpoints.up('xl')]: { width: '50%' },
          })}
        >
          <Box sx={{ p: 2 }}>
            <ModalObjectCard object={object} onClose={handleCloseModal} source="request" />
          </Box>
        </Box>
      </Modal>
      {/* Dialog подтверждения удаления */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{'Подтверждение удаления'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Удалить этот объект из заявки?
            <br />
            (это неприведет к удалению объекта из вашей базы)
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Отмена
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default observer(ListObjectActiveRequest);
